import { useTenantConfig } from 'contexts';
import { isServerless } from 'helpers';
import Script from 'next/script';
import React, { useEffect, useState } from 'react';
import { useCookieConsentStore } from 'stores';

/**
 * Zalo SDK has no consent API to suppress cookies, so we do NOT load it from `_document` (denied by default on first load).
 * Instead we render the SDK only after the user grants consent.
 * When the flag is off, the SDK keeps loading from `_document` and this component renders nothing.
 *
 * CLS: the Zalo OA widget injects a cross-origin iframe whose greeting bubble morphs
 * (217x38 -> 250x125 -> 50x50) using position/size animation (not transform), which we
 * cannot style. We defer the SDK until the first user interaction. This does NOT rely on
 * CLS's post-input exclusion (that only covers shifts within 500ms of input); the point is:
 *  - lab tools (Lighthouse/PSI) never interact -> the widget never loads -> lab CLS stays clean;
 *  - in the field, the bubble morph is pushed out of the initial load window where it otherwise
 *    stacks with other shifts, and only fires for users who actually engage.
 */

const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

export const ZaloSdkScript: React.FC = () => {
  const { zaloOAId, epicOmni1936 } = useTenantConfig();
  const isZaloCookiesConsented = useCookieConsentStore((state) =>
    state.functionalityStorage(),
  );
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    if (interacted) return;
    const interact = () => setInteracted(true);
    INTERACTION_EVENTS.forEach((e) =>
      window.addEventListener(e, interact, { once: true, passive: true }),
    );
    return () =>
      INTERACTION_EVENTS.forEach((e) =>
        window.removeEventListener(e, interact),
      );
  }, [interacted]);

  if (
    !interacted ||
    !isServerless() ||
    !zaloOAId?.length ||
    !epicOmni1936 ||
    !isZaloCookiesConsented
  ) {
    return null;
  }

  return (
    <Script
      id="zalo-sdk-script"
      strategy="lazyOnload"
      src="https://sp.zalo.me/plugins/sdk.js"
    />
  );
};
